import React, { useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { listCognitoUsers, listCognitoGroups } from '../api/users';
import {
  enableUser,
  disableUser,
  resetUserPassword,
  deleteUser,
  createUser,
  updateUser,
  setUserRole
} from '../api/adminUsers';
import ConfirmationModal from '../components/ConfirmationModal';
import ActionDropdown from '../components/ActionDropdown';
import Modal from '../components/Modal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import UserFormModal from '../components/UserFormModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import RoleModal from '../components/RoleModal';
import useDebounce from '../hooks/useDebounce';

const UserManagement = ({ setAlert }) => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [paginationToken, setPaginationToken] = useState();
  const [showCreate, setShowCreate] = useState(false);
  const [sortField, setSortField] = useState('Username');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [confirmation, setConfirmation] = useState({ open: false, title: '', message: '' });
  const [editUserModal, setEditUserModal] = useState(null);
  const [resetUserModal, setResetUserModal] = useState(null);
  const [roleModal, setRoleModal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState('');

  useEffect(() => {
    loadUsers();
    listCognitoGroups()
      .then((g) => setGroups(g.Groups || []))
      .catch((e) => console.error('Failed to load groups', e));
  }, []);

  useEffect(() => {
    setPage(1);
    loadUsers();
  }, [debouncedSearch, selectedGroup]);


  const normalizeUser = (u) => {
    if (u.Username && u.Attributes) {
      return {
        ...u,
        UserStatus: u.UserStatus || u.userStatus || u.status
      };
    }

    const normalGroups = (() => {
      if (!u.groups) return undefined;
      if (Array.isArray(u.groups)) {
        return u.groups
          .map(g => {
            if (typeof g === 'string') return g;
            if (typeof g === 'object' && g) {
              return g.groupName || g.name || g.GroupName || '';
            }
            return '';
          })
          .filter(Boolean)
          .join(',');
      }
      if (typeof u.groups === 'object') {
        return u.groups.groupName || u.groups.name || u.groups.GroupName || '';
      }
      return u.groups;
    })();

    return {
      Username: u.username || u.Username,
      Enabled: typeof u.enabled === 'boolean' ? u.enabled : true,
      UserStatus: u.UserStatus || u.userStatus || u.status,
      Attributes: [
        u.email ? { Name: 'email', Value: u.email } : null,
        normalGroups ? { Name: 'custom:groups', Value: normalGroups } : null,
      ].filter(Boolean),
    };
  };

  const loadUsers = async (tokenArg) => {
    setUsersLoading(true);
    try {
      const data = await listCognitoUsers(
        selectedGroup || null,
        debouncedSearch || null,
        tokenArg
      );
      let loaded = data.Users || data.users || data;
      if (Array.isArray(loaded)) {
        loaded = loaded.map(normalizeUser);
      } else if (loaded && Array.isArray(loaded.Users)) {
        loaded = loaded.Users.map(normalizeUser);
      } else {
        loaded = [];
      }
      if (tokenArg) {
        setUsers((prev) => [...prev, ...loaded]);
      } else {
        setUsers(loaded);
      }
      setPaginationToken(
        data.paginationToken || data.NextToken || data.nextToken || null
      );
    } catch (e) {
      console.error('Failed to load users', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load users" });
      }
    } finally {
      setUsersLoading(false);
    }
  };


  const handleEnable = (u) => {
    setConfirmAction({
      title: 'Enable User',
      message: `Enable ${u.Username}?`,
      onConfirm: async () => {
        await enableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };

  const handleDisable = (u) => {
    setConfirmAction({
      title: 'Disable User',
      message: `Disable ${u.Username}?`,
      onConfirm: async () => {
        await disableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };


  const handleReset = (u) => {
    setResetUserModal(u);
  };

  const handleDelete = (u) => {
    setDeleteUserName(u.Username);
  };

  const confirmDeleteUser = async () => {
    await deleteUser(deleteUserName);
    setDeleteUserName('');
    loadUsers();
  };

  const handleSetRole = (u) => {
    setRoleModal(u);
  };

  const handleEdit = (u) => {
    setEditUserModal(u);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (dir) => {
    setPage((p) => {
      const total = Math.ceil(sorted.length / PAGE_SIZE) || 1;
      const next = p + dir;
      if (next < 1) return 1;
      if (next > total) return total;
      return next;
    });
  };


  const filtered = users.filter(u =>
    u.Username.toLowerCase().includes(search.toLowerCase()) ||
    (u.Attributes?.find(a => a.Name === 'email')?.Value || '').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let aVal;
    let bVal;
    switch (sortField) {
      case 'Email':
        aVal = a.Attributes?.find(a => a.Name === 'email')?.Value || '';
        bVal = b.Attributes?.find(a => a.Name === 'email')?.Value || '';
        break;
      case 'Status':
        aVal = a.UserStatus;
        bVal = b.UserStatus;
        break;
      case 'Username':
      default:
        aVal = a.Username;
        bVal = b.Username;
    }
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);


  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold">User Management</h1>
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 w-full md:w-auto">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              className="input-field pl-10 w-full"
              placeholder="Search users"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input-field sm:w-48"
            value={selectedGroup}
            onChange={e => {
              setSelectedGroup(e.target.value);
              setPaginationToken(null);
              loadUsers();
            }}
          >
            <option value="">All Groups</option>
            {groups.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <button onClick={() => setShowCreate(true)} className="btn-primary whitespace-nowrap">Create User</button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th onClick={() => handleSort('Username')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">Username {sortField === 'Username' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
            <th onClick={() => handleSort('Email')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">Email {sortField === 'Email' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
            <th onClick={() => handleSort('Status')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">Status {sortField === 'Status' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
            <th className="px-6 py-3 text-left font-semibold text-gray-700">Groups</th>
            <th className="px-6 py-3" />
          </tr>
        </thead>
        {usersLoading ? (
          <tbody>
            <tr>
              <td colSpan="5" className="px-6 py-4 text-center">Loading users...</td>
            </tr>
          </tbody>
        ) : (
          <tbody className="bg-white divide-y divide-gray-200">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No users found</td>
              </tr>
            ) : paginated.map(u => {
              const email = u.Attributes?.find(a => a.Name === 'email')?.Value || '';
              const groups = u.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '';
              const isDisabled = u.Enabled === false;
              const status = u.UserStatus === 'UNCONFIRMED' ? 'Unconfirmed' : (isDisabled ? 'Disabled' : 'Enabled');
              return (
                <tr key={u.Username} className="hover:bg-gray-50 odd:bg-white even:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{u.Username}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-badge ${status === 'Enabled' ? 'status-active' : status === 'Disabled' ? 'status-inactive' : 'status-pending'}`}>{status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{groups}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <ActionDropdown
                      actions={[
                        isDisabled
                          ? { label: 'Enable', onClick: () => handleEnable(u) }
                          : { label: 'Disable', onClick: () => handleDisable(u) },
                        { label: 'Set Role', onClick: () => handleSetRole(u) },
                        { label: 'Edit', onClick: () => handleEdit(u) },
                        { label: 'Reset PW', onClick: () => handleReset(u) },
                        { label: 'Delete', onClick: () => handleDelete(u), className: 'text-red-600' },
                      ]}
                    />
                  </td>
                </tr>
            );
            })}
          </tbody>
        )}
      </table>
      </div>
      {paginationToken && (
        <div className="mt-4 text-center">
          <button onClick={() => loadUsers(paginationToken)} className="btn-outline">Load More</button>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="flex justify-end items-center space-x-3 mt-4">
          <button onClick={() => handlePageChange(-1)} disabled={page === 1} className="btn-outline text-sm">Prev</button>
          <span className="text-sm">Page {page} of {totalPages}</span>
          <button onClick={() => handlePageChange(1)} disabled={page >= totalPages} className="btn-outline text-sm">Next</button>
        </div>
      )}

      <UserFormModal
        open={showCreate}
        title="Create User"
        onClose={() => setShowCreate(false)}
        onSubmit={async (data) => {
          try {
            await createUser({
              username: data.username,
              email: data.email,
              temporaryPassword: data.password || undefined
            });
            setConfirmation({ open: true, title: 'User Created', message: 'The user was created successfully.' });
            setShowCreate(false);
            loadUsers();
          } catch (err) {
            console.error('Failed to create user', err);
            setConfirmation({ open: true, title: 'Error', message: "Couldn't create user" });
          }
        }}
      />

      {editUserModal && (
        <UserFormModal
          open={!!editUserModal}
          title={`Edit ${editUserModal.Username}`}
          groups={groups}
          initial={{
            username: editUserModal.Username,
            email: editUserModal.Attributes?.find(a => a.Name === 'email')?.Value || '',
            groups: (editUserModal.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '').split(',').filter(Boolean)
          }}
          onClose={() => setEditUserModal(null)}
          onSubmit={async (data) => {
            try {
              await updateUser(editUserModal.Username, { email: data.email });
              setEditUserModal(null);
              setConfirmation({ open: true, title: 'User Updated', message: 'User updated successfully.' });
              loadUsers();
            } catch (err) {
              console.error('Failed to update user', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't update user" });
            }
          }}
        />
      )}

      {resetUserModal && (
        <ResetPasswordModal
          open={!!resetUserModal}
          username={resetUserModal.Username}
          onClose={() => setResetUserModal(null)}
          onSubmit={async (pw) => {
            try {
              await resetUserPassword(resetUserModal.Username, pw);
              setResetUserModal(null);
              setConfirmation({ open: true, title: 'Password Reset', message: 'Temporary password set.' });
            } catch (err) {
              console.error('Failed to reset password', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't reset password" });
            }
          }}
        />
      )}

      {roleModal && (
        <RoleModal
          open={!!roleModal}
          username={roleModal.Username}
          onClose={() => setRoleModal(null)}
          onSubmit={async (role) => {
            try {
              await setUserRole(roleModal.Username, role);
              setRoleModal(null);
              setConfirmation({ open: true, title: 'Role Updated', message: 'Role updated successfully.' });
              loadUsers();
            } catch (err) {
              console.error('Failed to set role', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't set role" });
            }
          }}
        />
      )}

      {confirmAction && (
        <Modal open={!!confirmAction} onClose={() => setConfirmAction(null)} title={confirmAction.title}>
          <p className="mb-4">{confirmAction.message}</p>
          <div className="flex justify-end space-x-2">
            <button onClick={() => setConfirmAction(null)} className="btn-outline">Cancel</button>
            <button
              onClick={async () => {
                await confirmAction.onConfirm();
              }}
              className="btn-primary"
            >
              Confirm
            </button>
          </div>
        </Modal>
      )}
      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => setConfirmation({ open: false, title: '', message: '' })}
      />
      <DeleteConfirmationModal
        open={!!deleteUserName}
        itemName={deleteUserName || ''}
        onClose={() => setDeleteUserName('')}
        onConfirm={confirmDeleteUser}
      />
    </div>
  );
};

export default UserManagement;
